import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  emptyFeeRuleForm,
  feeRuleFormHasValues,
  type AccountFeeRuleForm,
} from '../lib/accountFeeRules'
import {
  MARKET_ONLY_FEE_SCOPES,
  STOCK_EXCHANGE_GROUPS,
  type StockExchangeGroup,
} from '../lib/tradeFeeScope'

type Appearance = 'classic' | 'studio'

function FeeRuleFields({
  form,
  currency,
  appearance,
  onChange,
  scopeKey,
}: {
  form: AccountFeeRuleForm
  currency: string
  appearance: Appearance
  onChange: (next: AccountFeeRuleForm) => void
  scopeKey: string
}) {
  const { t } = useTranslation('accounts')

  const setField = (key: keyof AccountFeeRuleForm, value: string) => {
    onChange({ ...form, [key]: value })
  }

  const fields: { key: keyof AccountFeeRuleForm; label: string; hint?: string; step?: string }[] = [
    { key: 'fixed_fee', label: t('feeRules.fixedFee'), hint: t('feeRules.fixedFeeHint') },
    { key: 'min_fee', label: t('feeRules.minFee'), hint: t('feeRules.minFeeHint') },
    { key: 'threshold_amount', label: t('feeRules.threshold', { currency }), hint: t('feeRules.thresholdHint') },
    { key: 'fee_below_threshold', label: t('feeRules.feeBelowThreshold', { currency }), hint: t('feeRules.feeBelowThresholdHint') },
    { key: 'percent_above_threshold', label: t('feeRules.percentAbove'), hint: t('feeRules.percentAboveHint'), step: '0.0001' },
  ]

  const wrapClass = appearance === 'studio' ? 'at-fee-rule-fields' : 'space-y-3'
  const rowClass = appearance === 'studio' ? 'at-fee-rule-field-row' : 'flex items-center gap-3 min-h-[36px]'
  const labelClass = appearance === 'studio'
    ? 'at-fee-rule-field-label'
    : 'flex-1 min-w-0 text-sm font-medium text-gray-700'
  const inputClass = appearance === 'studio'
    ? undefined
    : 'w-28 sm:w-36 shrink-0 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500'
  const hintClass = appearance === 'studio' ? 'at-fee-rule-field-hint' : 'text-xs text-gray-400 pl-0'

  return (
    <div className={wrapClass}>
      {fields.map((field) => {
        const inputId = `fee-${scopeKey}-${field.key}`
        return (
          <div key={field.key} className={appearance === 'studio' ? 'at-fee-rule-field' : ''}>
            <div className={rowClass}>
              <label htmlFor={inputId} className={labelClass}>{field.label}</label>
              <input
                id={inputId}
                type="number"
                min="0"
                step={field.step || '0.01'}
                value={form[field.key]}
                onChange={(event) => setField(field.key, event.target.value)}
                placeholder="0"
                className={inputClass}
              />
            </div>
            {field.hint && <p className={hintClass}>{field.hint}</p>}
          </div>
        )
      })}
    </div>
  )
}

function FeeScopeAccordion({
  scopeKey,
  title,
  hint,
  open,
  onToggle,
  form,
  currency,
  appearance,
  onFormChange,
}: {
  scopeKey: string
  title: string
  hint?: string
  open: boolean
  onToggle: () => void
  form: AccountFeeRuleForm
  currency: string
  appearance: Appearance
  onFormChange: (next: AccountFeeRuleForm) => void
}) {
  const hasValues = feeRuleFormHasValues(form)
  const triggerClass = appearance === 'studio'
    ? `at-fee-accordion-trigger${open ? ' open' : ''}`
    : 'w-full flex items-start justify-between gap-3 px-3 py-2.5 text-left rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 transition-colors'
  const panelClass = appearance === 'studio' ? 'at-fee-accordion-panel' : 'px-3 pb-3 pt-2 border border-t-0 border-gray-200 rounded-b-lg bg-white'

  return (
    <div className={appearance === 'studio' ? 'at-fee-accordion' : 'rounded-lg overflow-hidden border border-gray-200'}>
      <button type="button" className={triggerClass} onClick={onToggle} aria-expanded={open}>
        <span className={appearance === 'studio' ? 'at-fee-accordion-copy' : 'min-w-0'}>
          <strong>{title}</strong>
          {hint && <small>{hint}</small>}
        </span>
        <span className={appearance === 'studio' ? 'at-fee-accordion-meta' : 'shrink-0 text-xs text-gray-500'} aria-hidden="true">
          {hasValues && !open ? '•' : ''}
          {open ? '−' : '+'}
        </span>
      </button>
      {open && (
        <div className={panelClass}>
          <FeeRuleFields
            scopeKey={scopeKey}
            form={form}
            currency={currency}
            appearance={appearance}
            onChange={onFormChange}
          />
        </div>
      )}
    </div>
  )
}

export default function AccountFeeRulesEditor({
  enabledScopes,
  onEnabledScopesChange,
  forms,
  onFormsChange,
  currency,
  appearance = 'classic',
}: {
  enabledScopes: string[]
  onEnabledScopesChange: (scopes: string[]) => void
  forms: Record<string, AccountFeeRuleForm>
  onFormsChange: (forms: Record<string, AccountFeeRuleForm>) => void
  currency: string
  appearance?: Appearance
}) {
  const { t } = useTranslation('accounts')
  const [expandedScopes, setExpandedScopes] = useState<string[]>(enabledScopes)

  useEffect(() => {
    setExpandedScopes(enabledScopes)
  }, [enabledScopes])

  const isOpen = (scopeKey: string) => expandedScopes.includes(scopeKey)

  const toggleScope = (scopeKey: string) => {
    if (isOpen(scopeKey)) {
      setExpandedScopes((current) => current.filter((key) => key !== scopeKey))
      onEnabledScopesChange(enabledScopes.filter((key) => key !== scopeKey))
      return
    }
    setExpandedScopes((current) => [...current, scopeKey])
    if (!enabledScopes.includes(scopeKey)) {
      onEnabledScopesChange([...enabledScopes, scopeKey])
    }
    if (!forms[scopeKey]) {
      onFormsChange({ ...forms, [scopeKey]: emptyFeeRuleForm() })
    }
  }

  const updateForm = (scopeKey: string, next: AccountFeeRuleForm) => {
    onFormsChange({ ...forms, [scopeKey]: next })
    if (!enabledScopes.includes(scopeKey)) {
      onEnabledScopesChange([...enabledScopes, scopeKey])
    }
    if (!expandedScopes.includes(scopeKey)) {
      setExpandedScopes((current) => [...current, scopeKey])
    }
  }

  const stockScopeKey = (group: StockExchangeGroup) => `Stock:${group}`

  const listClass = appearance === 'studio' ? 'at-fee-rules-accordion-list' : 'space-y-2'

  return (
    <div className={appearance === 'studio' ? 'at-fee-rules-editor' : 'border-t border-gray-100 pt-4 mt-2 space-y-4'}>
      <div>
        <strong className={appearance === 'studio' ? 'at-fee-rules-title' : 'text-sm font-semibold text-gray-900'}>
          {t('feeRules.title')}
        </strong>
        <p className={appearance === 'studio' ? 'at-fee-rules-subtitle' : 'text-xs text-gray-500 mt-1'}>
          {t('feeRules.subtitleScopes')}
        </p>
      </div>

      <div className={listClass}>
        <p className={appearance === 'studio' ? 'at-fee-rules-section-label' : 'text-[10px] font-bold uppercase tracking-wide text-gray-500 px-1'}>
          {t('feeRules.stockSection')}
        </p>
        {STOCK_EXCHANGE_GROUPS.map((group) => {
          const scopeKey = stockScopeKey(group.id)
          return (
            <FeeScopeAccordion
              key={scopeKey}
              scopeKey={scopeKey}
              title={group.label}
              hint={group.hint}
              open={isOpen(scopeKey)}
              onToggle={() => toggleScope(scopeKey)}
              form={forms[scopeKey] || emptyFeeRuleForm()}
              currency={currency}
              appearance={appearance}
              onFormChange={(next) => updateForm(scopeKey, next)}
            />
          )
        })}

        {MARKET_ONLY_FEE_SCOPES.map((entry) => {
          const scopeKey = entry.market
          return (
            <FeeScopeAccordion
              key={scopeKey}
              scopeKey={scopeKey}
              title={entry.label}
              open={isOpen(scopeKey)}
              onToggle={() => toggleScope(scopeKey)}
              form={forms[scopeKey] || emptyFeeRuleForm()}
              currency={currency}
              appearance={appearance}
              onFormChange={(next) => updateForm(scopeKey, next)}
            />
          )
        })}
      </div>
    </div>
  )
}
